import { Accordion } from '@base-ui/react/accordion'
import { Link } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { PlusIcon } from 'lucide-react'
import { ContactFormOnAppear } from './contact-form-on-appear'
import { LANDING_FAQ } from './faq-content'
import { Reveal } from './reveal'

/**
 * The page's close, in reading order: the questions companies ask before starting on the left (first when stacked), then
 * the contact form on the right, for whatever the answers leave open or a demo. The answers are an accordion, one open at
 * a time.
 */
export function FaqSection() {
  const { t, i18n } = useLingui()
  const faqs = LANDING_FAQ.map((entry) => ({
    question: i18n._(entry.question),
    answer: i18n._(entry.answer),
    docsLink: entry.docsLink,
  }))

  return (
    <section id="faq" className="scroll-mt-20 pt-20 pb-32 [--muted-foreground:var(--foreground)]">
      <div className="mx-auto grid max-w-6xl gap-14 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{t`FAQ`}</h2>
          {/* One answer open at a time; the panel's height animates from Base UI's measured `--accordion-panel-height`,
              and closed answers stay `hidden="until-found"`, so find-in-page still opens them. */}
          <Accordion.Root multiple={false} hiddenUntilFound className="mt-6 grid">
            {faqs.map((faq) => (
              <Accordion.Item key={faq.question} className="border-b">
                <Accordion.Header className="m-0">
                  <Accordion.Trigger className="group flex w-full cursor-pointer items-start gap-4 py-4.5 text-left leading-snug font-semibold outline-none focus-visible:underline">
                    {faq.question}
                    <PlusIcon className="text-primary ml-auto size-5 shrink-0 transition-transform duration-300 ease-out group-data-[panel-open]:rotate-45" />
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-300 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 motion-reduce:transition-none">
                  <p className="pr-10 pb-5 text-sm leading-relaxed">
                    {faq.answer}
                    {/* The reference itself, for the reader this answer has just told what the API does. */}
                    {faq.docsLink && (
                      <>
                        {' '}
                        <Link to="/docs" className="text-primary font-medium underline-offset-4 hover:underline">
                          {t`See the documentation`}
                        </Link>
                      </>
                    )}
                  </p>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </Reveal>

        <Reveal delay={140}>
          <div id="contact" className="scroll-mt-24">
            <h2 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{t`Contact us`}</h2>
            <p className="text-foreground mt-3 mb-8 text-lg leading-relaxed text-pretty">
              {t`Have questions, or want to see the platform in action? Write to us or book a demo to discover how to take control of talent in your company.`}
            </p>
            <ContactFormOnAppear compact />
          </div>
        </Reveal>
      </div>
    </section>
  )
}
